import { useState } from 'react';
import { ApiError, auth, setAccessToken } from '@tasca/client-sdk';
import { useStore } from '@tasca/client-sdk';

const MESSAGES: Record<string, string> = {
  invalid_credentials: 'Credenciais inválidas.',
  username_taken: 'Esse nome de utilizador já está em uso.',
  email_taken: 'Já existe uma conta com esse email.',
  validation_failed: 'Verifica os campos.',
};

/** O servidor sabe quantos segundos faltam — dizer "espera um pouco" quando
 *  temos o numero exacto deixa o utilizador sem saber se sao 5s ou uma hora. */
function waitMessage(details: unknown): string {
  const seconds = (details as { retryAfter?: number } | undefined)?.retryAfter;
  if (!seconds) return 'Demasiadas tentativas. Tenta mais tarde.';
  if (seconds < 90) return `Demasiadas tentativas. Tenta dentro de ${seconds}s.`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 90) return `Demasiadas tentativas. Tenta dentro de ${minutes} min.`;
  return `Demasiadas tentativas. Tenta dentro de ${Math.ceil(minutes / 60)}h.`;
}

export function Auth(): React.ReactElement {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const setMe = useStore((s) => s.setMe);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result =
        mode === 'login'
          ? await auth.login({ identifier, password })
          : await auth.register({ username, email, password });
      setAccessToken(result.accessToken);
      setMe(result.user);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.code === 'rate_limited'
            ? waitMessage(err.details)
            : (MESSAGES[err.code] ?? err.message ?? 'Falhou. Tenta outra vez.'),
        );
      } else {
        setError('Sem ligação ao servidor.');
      }
    } finally {
      setBusy(false);
    }
  }

  const canSubmit =
    mode === 'login'
      ? identifier.length > 0 && password.length > 0
      : username.length >= 2 && email.includes('@') && password.length >= 12;

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-head">
          <div className="mark">T</div>
          <div>
            <h1>Tasca</h1>
            <p>{mode === 'login' ? 'Entra na tua conta' : 'Cria uma conta'}</p>
          </div>
        </div>

        {mode === 'login' ? (
          <label>
            Utilizador ou email
            <input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              autoFocus
            />
          </label>
        ) : (
          <>
            <label>
              Nome de utilizador
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                placeholder="minusculas, digitos, _ e ."
                autoComplete="username"
                autoFocus
              />
            </label>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
          </>
        )}

        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
          {mode === 'register' && (
            <small>
              Mínimo 12 caracteres. O comprimento é o que resiste a força bruta — não há
              regras de composição.
            </small>
          )}
        </label>

        {error && <p className="auth-error">{error}</p>}

        <button type="submit" disabled={!canSubmit || busy}>
          {busy ? 'A processar…' : mode === 'login' ? 'Entrar' : 'Criar conta'}
        </button>

        <button
          type="button"
          className="link"
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            setError(null);
          }}
        >
          {mode === 'login' ? 'Não tenho conta' : 'Já tenho conta'}
        </button>
      </form>
    </div>
  );
}
